import React from 'react';
import Link from 'next/link';
import { ArrowRightIcon } from '@heroicons/react/20/solid';
import { useTranslations } from 'next-intl';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import { ArrayElementType } from '@/integrations/crystallize/types/helpers/ArrayElementType';
import { HeaderFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { appendPropertiesTableComponentToUrl } from '@/utils/url-helper';
import { buildTrackingId } from '@/utils/tracking-helper';
import { MobileSecondLevelMenuProps } from '../MobileSecondLevelMenu/types/MobileSecondLevelMenuProps';

type HeaderLink = ArrayElementType<HeaderFragment['children']>;

interface HeaderMobileMenuItemProps {
  link: HeaderLink;
  close: () => void;
  setSecondLevelMenu: (menu: MobileSecondLevelMenuProps['menuData']) => void;
}

export function HeaderMobileMenuItem({
  link,
  close,
  setSecondLevelMenu,
}: Readonly<HeaderMobileMenuItemProps>) {
  const { localizedLink } = useLocaleLink();
  const t = useTranslations('Header');

  if (link.__typename !== 'Document') {
    return null;
  }

  const externalLink =
    link.directExternalLink?.content?.__typename === 'SingleLineContent'
      ? link.directExternalLink.content.text
      : null;
  const internalLink =
    link.directInternalLink?.content?.__typename === 'ItemRelationsContent'
      ? link.directInternalLink.content.items?.[0]?.path
      : null;
  const hasLink = externalLink || internalLink;

  const linkComponentsContent = link.components?.content;
  const linkComponents =
    linkComponentsContent?.__typename === 'ComponentMultipleChoiceContent'
      ? linkComponentsContent.selectedComponents
      : [];

  const openSecondLevel = () => {
    setSecondLevelMenu({
      title: link.documentName,
      components: linkComponents,
    });
  };

  const handleLinkClick = () => close();

  const linkTrackingId = buildTrackingId('nav-mobile', link.documentName);
  const submenuTrackingId = buildTrackingId('nav-mobile', link.documentName, 'open-submenu');

  let labelNode: React.ReactNode;
  if (hasLink) {
    labelNode = (
      <Link
        onClick={handleLinkClick}
        className="text-2xl font-medium"
        href={appendPropertiesTableComponentToUrl(
          hasLink.startsWith('mailto:') ? hasLink : localizedLink(hasLink),
          link.linkParameters,
        )}
        data-tile-element-id={linkTrackingId}
      >
        {link.documentName}
      </Link>
    );
  } else if (linkComponents.length > 0) {
    labelNode = (
      <button
        type="button"
        onClick={openSecondLevel}
        className="text-left"
        data-tile-element-id={linkTrackingId}
      >
        {link.documentName}
      </button>
    );
  } else {
    labelNode = <div data-tile-element-id={linkTrackingId}>{link.documentName}</div>;
  }

  return (
    <li>
      <div className="flex w-full items-center justify-between text-2xl font-medium">
        {labelNode}
        {linkComponents.length > 0 && (
          <button
            type="button"
            onClick={openSecondLevel}
            aria-label={t('openSubmenu')}
            data-tile-element-id={submenuTrackingId}
          >
            <ArrowRightIcon className="h-6 w-auto text-primary-100" aria-hidden="true" />
          </button>
        )}
      </div>
    </li>
  );
}
